import { Button, IconButton, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { HiMiniLanguage } from "react-icons/hi2";
import { useNavigate, useParams } from "react-router-dom";
import { iLocation, Location as LocationModel } from "../../models/Location";
import api from "../../services/axios-client";
import CircularWithValueLabel from "../../components/CircularWithValueLabel";
import { uploadAsset } from "../../utilities/assetUtils";
import { ASSETS_PATH } from "../../constants";
import { createPortal } from "react-dom";
import PopLanguages from "../../components/PopLanguages";
import PopNarations from "../../components/PopNarations";
import { on } from "events";

const Location = () => {
  const locationId = useParams().location_id;
  const navigate = useNavigate();
  const [location, setLocation] = useState<iLocation>(new LocationModel());
  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [locationNamePop, setLocationNamePop] = useState<boolean>(false);
  const [locationAboutAudioPop, setLocationAboutAudioPop] = useState<boolean>(
    false
  );
  const [locationExtendedAudioPop, setLocationExtendedAudioPop] = useState<boolean>(
    false
  );

  useEffect(() => {
    if (locationId !== "0") {
      fetchLocation();
    }
  }, [locationId]);

  const fetchLocation = async () => {
    try {
      const response = await api.get(`/locations/${locationId}`);
      if (response.status === 200) setLocation(response.data.location);
    } catch (error) {
      console.log(error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setLocation((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const onCoverImage = (e: any) => {
    uploadAsset(
      e,
      (fileName: string) => {
        setLocation({ ...location, location_image: fileName });
      },
      setUploading,
      setProgress
    );
  };


  // Location Name
  const onLocationNameLsPopOpen = () => {
    setLocationNamePop(true);
  };

  const onLocationNameLsPopClose = () => {
    setLocationNamePop(false);
  };

  const onSetLocationLs = (ls: any) => {
    setLocation({ ...location, location_name_ls: ls });  
    setLocationNamePop(false);
  }


  // Location About Audio
  const onLocationAboutAudioPopOpen = () => {
    setLocationAboutAudioPop(true);
  };

  const onLocationAboutAudioPopClose = () => {
    setLocationAboutAudioPop(false);
  };

  const onSetLocationAboutAudio = (audio: any) => {
    setLocation({ ...location, location_audio_intro: audio });
    onLocationAboutAudioPopClose();
  };


  // Loaction Ext audio
  const onLocationExtAudioPopOpen = () => {
    setLocationExtendedAudioPop(true);
  };

  const onLocationExtAudioPopClose = () => {
    setLocationExtendedAudioPop(false);
  };

  const onSetLocationExtAudio = (audio: any) => {
    setLocation({ ...location, location_audio_extended: audio });
    onLocationExtAudioPopClose();
  };


  const onSubmit = async () => {
    if (!location.location_name.trim()) {
      alert("Location Name is mandatory.");
      return;
    }

    const nameRegex = /^[a-zA-Z0-9 ]+$/;
    if (!nameRegex.test(location.location_name)) {
      alert("Location Name can only contain letters, numbers, and spaces.");
      return;
    }

    if (location.location_beacon_id) {      
      if (!/^[A-Z0-9]+$/.test(location.location_beacon_id)) {
        alert(
          "Beacon ID can only contain capital letters and numbers."
        );
        return;
      }
    }

    try {
      if (locationId !== "0") {
        await api.put(`/locations/${locationId}`, location);
        alert("Location updated successfully!");
      } else {
        await api.post("/locations", location);
        alert("Location created successfully!");
      }
      navigate("/locations");
    } catch (error) {
      console.error("Failed to save location:", error);
      alert("An error occurred while saving the location.");
    }
  };

  return (
    <>
      <div className="content-header pb-6">Location Name</div>
      <div className="content-body">
        <div className="flex flex-col gap-4 max-w-[600px]">
          <div className="flex flex-row">
            <TextField
              variant="outlined"
              label="Location Name*"
              name="location_name"
              value={location.location_name}
              onChange={handleInputChange}
              fullWidth
            />
            <IconButton
              aria-label="Add space name in other languages"
              onClick={onLocationNameLsPopOpen}
            >
              <HiMiniLanguage className="l-icon" />
            </IconButton>
          </div>
          <TextField
            variant="outlined"
            label="Beacon ID"
            name="location_beacon_id"
            value={location.location_beacon_id || ""}
            onChange={handleInputChange}
            fullWidth
          />
          {location.location_image && (
            <img
              src={`${ASSETS_PATH}/${location.location_image}`}
              alt=""
              width={600}
              height={400}
            />
          )}
          {!location.location_image && (
            <Button
              variant="contained"
              component="label"
              style={{ marginRight: "10px" }}
            >
              *Upload Cover Image
              <input type="file" hidden onChange={onCoverImage} />
            </Button>
          )}
          {location.location_image && (
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setLocation({ ...location, location_image: "" });
              }}
            >
              Remove cover image
            </Button>
          )}
          <Button variant="contained" color="primary" onClick={onLocationAboutAudioPopOpen}>
            Upload Intro Audio
          </Button>
          <Button variant="contained" color="primary" onClick={onLocationExtAudioPopOpen}>
            Upload Extended Audio
          </Button>
          <TextField
            multiline
            variant="outlined"
            minRows={4}
            label="Description"
            name="location_desc"
            value={location.location_desc}
            onChange={handleInputChange}
            fullWidth
          />
          <hr />
          <Button variant="contained" color="primary" onClick={onSubmit}>
            {locationId !== "0" ? "Update Location" : "Add Location"}
          </Button>
        </div>
      </div>
      {uploading && <CircularWithValueLabel progress={progress} />}
      {locationNamePop &&
        createPortal(
          <PopLanguages
            fieldName={`Space Name`}
            languages={location.location_name_ls || {}}
            onSetLs={onSetLocationLs}
            onLangPopClose={onLocationNameLsPopClose}
          />,
          document.getElementById("modal") as HTMLElement
        )}
      {locationAboutAudioPop &&
        createPortal(
          <PopNarations
            narrations={location.location_audio_intro || {}}
            onSetNarrations={onSetLocationAboutAudio}
            onNarrationsPopClose={onLocationAboutAudioPopClose}
          />,
          document.getElementById("modal") as HTMLElement
        )}
      {locationExtendedAudioPop &&
        createPortal(
          <PopNarations
            narrations={location.location_audio_extended || {}}
            onSetNarrations={onSetLocationExtAudio}
            onNarrationsPopClose={onLocationExtAudioPopClose}
          />,
          document.getElementById("modal") as HTMLElement
        )}
    </>
  );
};

export default Location;
